import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TbEdit } from "react-icons/tb";

import editIcon from "../../assets/icons/edit.svg";
import deleteIcon from "../../assets/icons/delete.svg";

import { useThunk } from "../../hooks/useThunk";
import {
    addSingleBanner,
    deleteSingleBanner,
    getAllBanners,
    updateSingleBanner,
    upsertBanners,
} from "../../services/thunks/bannerThunks";
import {
    uploadToCloudflare,
    deleteFromCloudflare,
} from "../../services/thunks/cloudFlareThunks";
import showToast from "../../utils/toastNotifications";
import { processImageFile } from "../../utils/processImageFile";

import Input from "../../components/Form/Input";
import CustomButton from "../../components/Buttons/CustomButton";
import Table from "../../components/Table/Table";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";
import CloudflareImage from "../../components/CloudflareImage";

const BannerManagement = () => {
    const auth = useSelector((state) => state.auth);
    const addRowRef = useRef();

    const [doUpsertBanners, isUpsertingBanners] = useThunk(upsertBanners);
    const [doUploadToCloudflare, isUploadingToCloudflare] =
        useThunk(uploadToCloudflare);
    const [doDeleteFromCloudflare, isDeletingFromCloudflare] =
        useThunk(deleteFromCloudflare);
    const [doUpdateSingleBanner, isUpdatingSingleBanner] = useThunk(updateSingleBanner);
    const [doAddSingleBanner, isAddingSingleBanner] = useThunk(addSingleBanner);
    const [doDeleteSingleBanner, isDeletingSingleBanner] = useThunk(deleteSingleBanner);

    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [showConfirm, setShowConfirm] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);
    const [rowStates, setRowStates] = useState({});
    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const [editMode, setEditMode] = useState(false);

    const tableRef = useRef(null);
    const tableDataRef = useRef([]);
    const pageSetterRef = useRef(null);
    const tableUpdateRowRef = useRef(null);
    const tableRemoveRowRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tableRef.current && !tableRef.current.contains(event.target)) {
                setRowStates({});
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleDelete = async (record) => {
        try {
            if (record._id.startsWith("new-")) {
                if (tableRemoveRowRef.current) {
                    tableRemoveRowRef.current(record._id);
                }

                setRowStates(prev => {
                    const newStates = { ...prev };
                    delete newStates[record._id];
                    return newStates;
                });

                showToast("success", "Row removed");
                return;
            }

            const result = await doDeleteSingleBanner({
                token: auth.token,
                bannerId: record._id,
            });

            if (!result.success) {
                showToast("error", result.error?.message || "Failed to delete banner");
                return;
            }

            showToast("success", "Banner deleted successfully.");

            if (tableRemoveRowRef.current) {
                tableRemoveRowRef.current(record._id);
            }

            const deletedImageUrl = result.data?.deletedBanner?.imageUrl || record.bannerImage;
            if (deletedImageUrl) {
                const imageKey = deletedImageUrl.split("/").pop();
                const deleteResult = await doDeleteFromCloudflare({ key: imageKey });
                if (!deleteResult.success) {
                    showToast(
                        "error",
                        deleteResult.error?.message || "Failed to delete image from Cloudflare"
                    );
                }
            }

            setRefreshTrigger((prev) => prev + 1);
        } catch (err) {
            console.error(err);
            showToast("error", "Failed to delete banner");
        }
    };


    const handleRowSave = React.useCallback(
        async (id, linkUrl, image) => {
            try {
                setEditMode(true);
                let bannerImageUrl = image;

                // If image is a File object, upload it first
                if (image instanceof File) {
                    const uploadResult = await doUploadToCloudflare({
                        file: image,
                        type: "images",
                    });

                    const url =
                        uploadResult?.response?.url ||
                        uploadResult?.data?.url ||
                        uploadResult?.payload?.url ||
                        uploadResult?.url;

                    if (!uploadResult.success || !url) {
                        showToast("error", "Failed to upload image");
                        return;
                    }
                    bannerImageUrl = url;
                }

                let result;

                // if id is temporary or not found in backend, add new banner
                if (id.startsWith("new-")) {
                    result = await doAddSingleBanner({
                        token: auth.token,
                        bannerData: {
                            imageUrl: bannerImageUrl,
                            linkUrl: linkUrl || null,
                            isVisible: true,
                        },
                    });
                } else {
                    //  update banner
                    result = await doUpdateSingleBanner({
                        token: auth.token,
                        bannerId: id,
                        bannerData: {
                            imageUrl: bannerImageUrl,
                            linkUrl: linkUrl || null,
                            isVisible: true,
                        },
                    });
                }

                if (!result.success) {
                    showToast("error", result.error?.message || "Failed to save banner");
                    return;
                }

                // Update local table data with the response
                if (tableDataRef.current && result.data?.allBanners) {
                    tableDataRef.current = result.data.allBanners.map(banner => ({
                        _id: banner._id,
                        bannerImage: banner.imageUrl,
                        linkUrl: banner.linkUrl,
                        order: banner.order,
                        isVisible: banner.isVisible
                    }));
                }

                // Update the table row
                if (tableUpdateRowRef.current) {
                    const newId =
                        id.startsWith("new-") && result.data?.banner?._id
                            ? result.data.banner._id
                            : id;

                    tableUpdateRowRef.current(newId, {
                        _id: newId,
                        bannerImage: bannerImageUrl,
                        linkUrl: linkUrl || null,
                    });
                }

                // Clear editing state
                setRowStates((prev) => {
                    const newStates = { ...prev };
                    delete newStates[id];
                    return newStates;
                });

                // Highlight updated row
                setHighlightedRowId(
                    id.startsWith("new-") && result.data?.banner?._id
                        ? result.data.banner._id
                        : id
                );
                setTimeout(() => setHighlightedRowId(null), 3000);

                showToast(
                    "success",
                    id.startsWith("new-")
                        ? "Banner added successfully"
                        : "Banner updated successfully"
                );

                // Refresh the table to ensure data consistency
                setRefreshTrigger((prev) => prev + 1);

            } catch (error) {
                console.error('Save error:', error);
                showToast("error", "Failed to save banner");
            } finally {
                setEditMode(false);
            }
        },
        [
            doUploadToCloudflare,
            doUpdateSingleBanner,
            doAddSingleBanner,
            setRowStates,
            setHighlightedRowId,
            tableUpdateRowRef,
        ]
    );

    const tableColumns = useMemo(() => {
        const columns = [
            {
                title: "Thumb / Image",
                dataIndex: "imageUrl",
                key: "imageUrl",
                align: "center",
                minWidth: "200px",
                render: (bannerImage, record) => {
                    // console.log("bannerImage", bannerImage)
                    console.log("record", record)
                    const isEditing = rowStates[record._id]?.editing;
                    const image = rowStates[record._id]?.image ?? bannerImage;
                    const error = rowStates[record._id]?.error;

                    if (isEditing) {
                        return (
                            <div className="flex flex-row items-start ml-10">
                                <div className="relative group w-[40px] h-[40px]">
                                    {image instanceof File ? (
                                        <img
                                            src={URL.createObjectURL(image)}
                                            alt="Banner preview"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <CloudflareImage imageUrl={image} alt="Banner" />
                                    )}
                                    <button
                                        onClick={() =>
                                            document.getElementById(`fileInput-${record._id}`).click()
                                        }
                                        className="absolute bottom-0 right-0 p-1 bg-slate-700 hover:bg-black text-white rounded-full transition"
                                    >
                                        <TbEdit className="text-white text-sm" />
                                    </button>
                                    <input
                                        id={`fileInput-${record._id}`}
                                        type="file"
                                        accept=".jpg,.jpeg,.png"
                                        className="hidden"
                                        onChange={(e) => {
                                            const selectedFile = e.target.files[0];
                                            if (!selectedFile) return;

                                            if (selectedFile.size > 3 * 1024 * 1024) {
                                                showToast(
                                                    "error",
                                                    "Image exceeds maximum allowed size of 3MB."
                                                );
                                                return;
                                            }

                                            setRowStates((prev) => ({
                                                ...prev,
                                                [record._id]: {
                                                    ...prev[record._id],
                                                    error: null,
                                                },
                                            }));

                                            processImageFile(selectedFile, {
                                                preserveOriginal: true, // ← Use original size, no cropping
                                                quality: 0.85,
                                            })
                                                .then((formattedFile) => {
                                                    if (formattedFile.size > 500 * 1024) {
                                                        showToast(
                                                            "error",
                                                            "Compressed image exceeds 500KB."
                                                        );
                                                        return;
                                                    }

                                                    setRowStates((prev) => ({
                                                        ...prev,
                                                        [record._id]: {
                                                            ...prev[record._id],
                                                            image: formattedFile,
                                                            error: null,
                                                        },
                                                    }));
                                                })
                                                .catch((err) => {
                                                    showToast(
                                                        "error",
                                                        err.message || "Image processing failed"
                                                    );
                                                });
                                        }}
                                    />
                                </div>
                                <div className="ml-4 text-red-500 text-xs mt-2 max-w-[200px] break-words">
                                    {error}
                                </div>
                            </div>
                        );
                    }

                    return (record.bannerImage || bannerImage) ? (
                        <div className="flex justify-start items-center ml-10">
                            <CloudflareImage imageUrl={record.bannerImage || bannerImage} alt="Banner" />
                        </div>
                    ) : (
                        <div className="ml-8 text-[#888] h-[40px] flex items-center text-sm italic">
                            No Image
                        </div>
                    );
                },
            },
            {
                title: "Link URL",
                dataIndex: "linkUrl",
                key: "linkUrl",
                minWidth: "300px",
                render: (text, record) => {
                    const isEditing = rowStates[record._id]?.editing;
                    const value = rowStates[record._id]?.linkUrl ?? text;

                    return isEditing ? (
                        <Input
                            autoFocus
                            type="text"
                            value={value || ""}
                            placeholder="Enter URL (optional)"
                            onChange={(e) => {
                                const newValue = e.target.value;
                                setRowStates((prev) => ({
                                    ...prev,
                                    [record._id]: {
                                        ...prev[record._id],
                                        linkUrl: newValue,
                                    },
                                }));
                            }}
                            inputStyle="bg-transparent border-0 border-b border-[#ccc] rounded-none shadow-none outline-none px-0 pb-0 pt-[1px] h-[28px] text-sm [400] leading-[18px] text-[#E2E2E2]font-inter"
                        />
                    ) : (
                        <div className="min-h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2]">
                            {text || "---"}
                        </div>
                    );
                },
            },
            {
                title: "Action",
                key: "action",
                minWidth: "160px",
                render: (_, record) => {
                    const isEditing = rowStates[record._id]?.editing;
                    const value = rowStates[record._id]?.linkUrl ?? record.linkUrl;
                    const image = rowStates[record._id]?.image ?? record.imageUrl;

                    return (
                        <div className="flex gap-3 justify-start items-center">
                            {isEditing ? (
                                <>
                                    <CustomButton
                                        onClick={() =>
                                            setRowStates((prev) => ({
                                                ...prev,
                                                [record._id]: {
                                                    editing: false,
                                                    value: undefined,
                                                    image: undefined,
                                                },
                                            }))
                                        }
                                        className="flex items-center justify-center gap-2 px-4 py-1 border border-[#CCCCCC] rounded-[15px] text-xs font-normal text-[#CCCCCC] w-[90px] hover:bg-[#333] transition font-inter"
                                    >
                                        Cancel
                                    </CustomButton>
                                    <CustomButton
                                        onClick={() => handleRowSave(record._id, value, image)}
                                        className="flex items-center justify-center gap-2 px-4 py-1 border border-[#00ffd5] rounded-[15px] text-xs font-normal text-[#00ffd5] w-[90px] hover:bg-[#00ffd53b] hover:text-white transition font-inter"
                                        disabled={
                                            !!rowStates[record._id]?.error ||
                                            isUpsertingBanners ||
                                            isUploadingToCloudflare
                                        }
                                        loading={isUpsertingBanners || isUploadingToCloudflare}
                                    >
                                        Save
                                    </CustomButton>
                                </>
                            ) : (
                                <>
                                    <CustomButton
                                        onClick={() => {
                                            setRowStates({
                                                [record._id]: {
                                                    editing: true,
                                                    linkUrl: record.linkUrl,
                                                    image: record.bannerImage,
                                                },
                                            });
                                        }}
                                        className="flex items-center justify-center gap-2 px-4 py-1 border border-[#B4C007] text-[#B4C007] rounded-[15px] text-xs font-normal w-[90px] hover:bg-[#333300] transition font-inter"
                                    >
                                        <img src={editIcon} alt="edit" className="w-3 h-3" />
                                        Edit
                                    </CustomButton>
                                    <CustomButton
                                        onClick={() => {
                                            setSelectedBanner(record);
                                            setShowConfirm(true);
                                        }}
                                        className="flex items-center justify-center gap-2 px-4 py-1 border border-[#C00707] text-[#C00707] rounded-[15px] text-xs font-normal w-[90px] hover:bg-[#330000] transition font-inter"
                                    >
                                        <img src={deleteIcon} alt="delete" className="w-3 h-3" />
                                        Delete
                                    </CustomButton>
                                </>
                            )}
                        </div>
                    );
                },
            },
        ];

        return columns;
    }, [
        rowStates,
        isUpsertingBanners,
        isUploadingToCloudflare,
        handleRowSave,
    ]);

    return (
        <div className="px-6 py-10 text-white min-h-screen">

            <div className="w-full flex justify-end">

                <CustomButton
                    onClick={() => {
                        const currentCount = tableDataRef.current?.length || 0;

                        if (currentCount >= 5) {
                            showToast("error", "You can only have up to 5 banners.");
                            return;
                        }

                        // Create a new row with empty fields
                        const newRow = {
                            _id: `new-${Date.now()}`,
                            imageUrl: null,   // empty image column
                            linkUrl: "",      // empty link URL column
                            order: (tableDataRef.current?.length || 0) + 1,
                            isVisible: true,
                        };

                        // Push it to the DataTable
                        if (addRowRef.current) {
                            addRowRef.current(newRow);
                        }

                        // Immediately put into editing state if neede  d:
                        setRowStates((prev) => ({
                            ...prev,
                            [newRow._id]: {
                                editing: true,
                                linkUrl: "",
                                image: null,
                            },
                        }));
                    }}
                    useGradientBorder
                    rounded="rounded-lg"
                    textSize="text-[18px] leading-[24px]"
                    padding="py-[10.5px] px-[13.5px]"
                    font="font-public-sans"
                    border="border-lg"
                    type="button"
                    width="w-[154px]"
                >
                    + Add Row
                </CustomButton>

            </div>

            {/* Table Section */}
            <div ref={tableRef}>
                <Table
                    columns={tableColumns}
                    fetchData={getAllBanners}
                    refreshTrigger={refreshTrigger}
                    rowKey="_id"
                    dataKey="items"
                    headingBgColor="#0D141B"
                    headingTextColor="#FFFFFF"
                    dataRef={tableDataRef}
                    highlightedRowId={highlightedRowId}
                    setPageCallback={(fn) => (pageSetterRef.current = fn)}
                    updateRowRef={tableUpdateRowRef}
                    removeRowRef={tableRemoveRowRef}
                    addRowRef={addRowRef}
                />
            </div>

            <ConfirmDeleteModal
                visible={showConfirm}
                onConfirm={async () => {
                    await handleDelete(selectedBanner);
                    setShowConfirm(false);
                    setSelectedBanner(null);
                }}
                onCancel={() => {
                    setShowConfirm(false);
                    setSelectedBanner(null);
                }}
                title="Confirm Deletion"
                content={`Are you sure you want to delete this banner?`}
                disabled={isDeletingFromCloudflare || isDeletingFromCloudflare}
                loading={isDeletingFromCloudflare || isDeletingFromCloudflare}
            />

        </div>
    );
};

export default BannerManagement;